import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const cardTitleTypography = {
  fontFamily: '"Noto Sans Hebrew"',
  fontWeight: 600,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  verticalAlign: "middle" as const,
  leadingTrim: "cap-height",
};

const cardValueTypography = {
  fontFamily: '"Noto Sans Hebrew" !important',
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  verticalAlign: "middle" as const,
  leadingTrim: "cap-height",
};

export const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "12px 16px",
    minWidth: 0,
    height: "100%",
    direction: "rtl",
    backgroundColor: theme.palette.background.paper,
  },
  cardActive: {
    backgroundColor: theme.palette.info.light,
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "8px",
    width: "100%",
    minHeight: 40,
    flexShrink: 0,
    direction: "rtl",
    "& .MuiCheckbox-root": {
      flexShrink: 0,
    },
  },
  cardTitle: {
    ...cardTitleTypography,
    color: theme.palette.secondary.main,
    direction: "rtl",
    flex: 1,
    minWidth: 0,
  },
  cardDivider: {
    width: "100%",
    height: 1,
    flexShrink: 0,
    backgroundColor: theme.palette.divider,
  },
  subCheckboxRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    width: "100%",
    direction: "rtl",
    "& .MuiCheckbox-root": {
      flexShrink: 0,
    },
  },
  subCheckboxLabel: {
    ...cardValueTypography,
    color: theme.palette.secondary.main,
    direction: "rtl",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    minWidth: 0,
    direction: "rtl",
    alignItems: "stretch",
  },
  fieldLabel: {
    fontFamily: '"Noto Sans Hebrew" !important',
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "80%",
    margin: 0,
    padding: 0,
    display: "block",
    color: theme.palette.text.primary,
    textAlign: "right",
    direction: "rtl",
    width: "100%",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: "10px",
    width: "100%",
    minWidth: 0,
    direction: "rtl",
    "& .MuiFormControlLabel-root": {
      flex: "1 1 0",
      minWidth: 0,
      maxWidth: "100%",
    },
  },
  radioOptionCard: {
    display: "flex",
    flex: "1 1 0",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    alignItems: "center",
    justifyContent: "flex-start",
    margin: 0,
    minHeight: 48,
    direction: "rtl",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "8px 12px",
    backgroundColor: theme.palette.background.paper,
    boxSizing: "border-box",
    overflow: "hidden",
    gap: "4px",
    "& .MuiRadio-root": {
      flexShrink: 0,
      padding: "4px",
    },
    "& .MuiFormControlLabel-label": {
      ...cardValueTypography,
      color: theme.palette.secondary.main,
      flex: 1,
      minWidth: 0,
      margin: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  noteInput: {
    direction: "rtl",
    margin: 0,
    width: "100%",
    "& .MuiOutlinedInput-root": {
      margin: 0,
      height: 48,
      boxSizing: "border-box",
      borderRadius: "4px",
      backgroundColor: theme.palette.background.paper,
      ...cardValueTypography,
      color: theme.palette.secondary.main,
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px 12px 8px",
      height: "100%",
      boxSizing: "border-box",
      direction: "rtl",
      ...cardValueTypography,
      color: theme.palette.secondary.main,
      "&::placeholder": {
        ...cardValueTypography,
        color: theme.palette.text.primary,
        opacity: 1,
      },
    },
  },
  fieldsRow: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    width: "100%",
    direction: "rtl",
    minWidth: 0,
    marginTop: "auto",
  },
  fieldsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    minWidth: 0,
    marginTop: "auto",
    direction: "rtl",
  },
  fieldFlex: {
    flex: 1,
    minWidth: 0,
  },
  fieldNarrow: {
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    flexShrink: 0,
    alignSelf: "flex-start",
  },
  stepperBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    direction: "ltr",
    width: "100%",
    height: 48,
    gap: 10,
    paddingTop: 12,
    paddingRight: 16,
    paddingBottom: 12,
    paddingLeft: 8,
    borderRadius: 4,
    opacity: 1,
    transform: "rotate(0deg)",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    boxSizing: "border-box",
  },
  stepperBoxQuantity: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    direction: "ltr",
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    alignSelf: "flex-start",
    flex: "0 0 auto",
    height: 48,
    gap: 10,
    paddingTop: 12,
    paddingRight: 16,
    paddingBottom: 12,
    paddingLeft: 8,
    borderRadius: 4,
    opacity: 1,
    transform: "rotate(0deg)",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    boxSizing: "border-box",
  },
  stepperArrows: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 24,
    height: 24,
    color: theme.palette.text.primary,
  },
  stepperIcon: {
    display: "block",
    width: 24,
    height: 24,
    color: theme.palette.text.primary,
  },
  stepperHitUp: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "50%",
    cursor: "pointer",
  },
  stepperHitDown: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "50%",
    cursor: "pointer",
  },
  stepperValue: {
    ...cardValueTypography,
    color: theme.palette.secondary.main,
    flex: 1,
  },
  radioField: {
    flex: 1,
    minWidth: 0,
  },
}));
